import { t } from "@redotech/redo-i18n";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { exchangeShippingRatesFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  ExchangeShippingRatesState,
  isExchangeShippingRatesStepValid,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import DeliveryTruckIcon from "@redotech/redo-web/icon-old/delivery-truck.svg";
import { produce } from "immer";
import * as React from "react";
import { StepDownstream, StepType, StepTypeDetailsProps } from "../step";

export const EXCHANGE_SHIPPING_RATES = (
  Details: React.ComponentType<
    StepTypeDetailsProps<ExchangeShippingRatesState>
  >,
): StepType<ExchangeShippingRatesState, ModelStep.ExchangeShippingRates> => ({
  Details: (props: StepTypeDetailsProps<ExchangeShippingRatesState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: STEP_TYPE_NAMES[ModelStep.EXCHANGE_SHIPPING_RATES],
  valid(state) {
    return isExchangeShippingRatesStepValid(state).valid;
  },
  Icon: DeliveryTruckIcon,
  iconColor: "success",
  description(state) {
    return state.rates.length
      ? t("Offer: {{rates}}", {
          rates: state.rates
            .map((rate) => rate.name || t("Unnamed rate"))
            .join(", "),
        })
      : t("Offer exchange shipping rates");
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  empty: { next: undefined, rates: [] },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for Exchange Shipping Rates step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.FULL;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.EXCHANGE_SHIPPING_RATES,
      next: state.next ? id(state.next) : undefined,
      rates: state.rates.map((rate) => ({
        id: rate.id,
        name: rate.name,
        description: rate.description || undefined,
        price: rate.price ?? 0,
      })),
    };
  },
  fromModel(model, id) {
    return exchangeShippingRatesFromModel(model, id);
  },
});
